import type { Event } from '@/types'
import { hasTime } from '@shared/dates'
import { hasEventStarted, isEventLive } from './formatDate'

const NO_END_LIVE_MS = 60 * 60 * 1000

// An event with a start time but no end counts as over an hour after it starts,
// rather than at the 2 AM default end.
function isLiveForHero(event: Event, now: Date): boolean {
  if (!event.endDate && hasTime(event.startDate)) {
    const start = new Date(event.startDate).getTime()
    return start <= now.getTime() && now.getTime() < start + NO_END_LIVE_MS
  }
  return isEventLive(event, now)
}

export interface HeroEvents {
  top: Event | null
  inline: Event | null
}

/**
 * `going` is sorted by start; one already underway counts until it's over. The
 * first festival and the first party get hero cards: whichever comes first leads
 * above the calendar, the other sits in its agenda slot.
 */
export function pickHeroEvents(going: Event[], now: Date): HeroEvents {
  const current = going.filter(e => !hasEventStarted(e, now) || isLiveForHero(e, now))
  const top = current[0] ?? null
  const inline = top ? current.find(e => e.type !== top.type) ?? null : null
  return { top, inline }
}

/**
 * Which of the user's events Home shows as hero cards: the ones they're going
 * to (not cancelled), by start. The single answer to "is this a hero" — Home
 * renders from it and the pre-game banner asks only about these.
 */
export function pickHomeHeroes(
  events: Event[],
  statuses: ReadonlyMap<string, string>,
  now: Date,
): HeroEvents {
  const going = events
    .filter(e => !e.cancelled && statuses.get(e.id) === 'going')
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
  return pickHeroEvents(going, now)
}
